import React, { useEffect, useRef, useState } from "react";
import gsap from "gsap";
import "./banner.scss";
import {
  callCrashGameGraphQLQuery,
  callGraphQLQuery,
} from "../../../utils/axiosClient";
import { sendEmail } from "../../../api/sendEmail";

const Banner = () => {
  const [crashGameData, setCrashGameData] = useState([]);
  const [oddsGameData, setOddsGameData] = useState([]);
  const [combinedTableData, setCombinedTableData] = useState([]);
  const [loading, setLoading] = useState(true);
  const containerRef = useRef(null);
  const boxWrapperRef = useRef(null);
  const tl = useRef(null);
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const [checked, setChecked] = useState(false);
  const [loadingEmail, setLoadingEmail] = useState(false);
  const [agree, setAgree] = useState(false);
  const [agreeError, setAgreeError] = useState("");
  const [request, setRequest] = useState(true);
const [submit, setSubmit] = useState(true); // false = show success message

  const fetchOddsGameData = async () => {
    const query = `
      query GetResolvedBets {
        betResolveds(orderBy: blockTimestamp, orderDirection: desc) {
          won
          player
          payout
          amount
          odds
          transactionHash
          blockTimestamp
          random
        }
      }
    `;
    try {
      const data = await callGraphQLQuery(query);
      setOddsGameData(data?.betResolveds || []);
    } catch (error) {
      console.error("Failed to fetch Odds game data:", error);
    }
  };

  const fetchCrashGameData = async () => {
    const query = `
      query PreviousRounds {
        previousRounds(orderBy: blockNumber, orderDirection: desc) {
          blockTimestamp
          transactionHash
          indexId
          rng
        }
      }
    `;
    try {
      const data = await callCrashGameGraphQLQuery(query);
      setCrashGameData(data?.previousRounds || []);
    } catch (error) {
      console.error("Failed to fetch Crash game data:", error);
    }
  };

  useEffect(() => {
    const combinedData = [
      ...crashGameData.map((game) => ({
        blockTimestamp: game.blockTimestamp,
        transactionHash: game.transactionHash,
        source: "Crash Game",
        player: "",
        result: game.rng,
      })),
      ...oddsGameData.map((bet) => ({
        blockTimestamp: bet.blockTimestamp,
        transactionHash: bet.transactionHash,
        source: "Odds Game",
        player: bet.player,
        result: bet.random,
      })),
    ];

    combinedData.sort((a, b) => b.blockTimestamp - a.blockTimestamp);
    setCombinedTableData(combinedData);
    setLoading(false);
  }, [crashGameData, oddsGameData]);

  useEffect(() => {
    fetchCrashGameData();
    fetchOddsGameData();
  }, []);

  useEffect(() => {
    const container = containerRef.current;
    const boxWrapper = boxWrapperRef.current;
    if (!container || !boxWrapper || combinedTableData.length === 0) return;

    tl.current?.kill();
    boxWrapper.innerHTML = "";

    combinedTableData.forEach((item) => {
      const box = document.createElement("div");
      box.className = "scroll-box";

      const shortHash = `${item.transactionHash.slice(
        0,
        6
      )}...${item.transactionHash.slice(-4)}`;
      const formattedDate = new Date(item.blockTimestamp * 1000).toLocaleString(
        "en-GB",
        {
          day: "2-digit",
          month: "short",
          year: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
        }
      );

      box.innerHTML = `
      <h6 class="boxhead">${item.result}</h6>
      <p class="boxpara">${formattedDate}</p>
      <a href="https://bscscan.com/tx/${item.transactionHash}" class="boxlink" target="_blank" rel="noopener noreferrer">${shortHash} <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
  <path d="M9.33333 2V3.33333H11.7267L5.17333 9.88667L6.11333 10.8267L12.6667 4.27333V6.66667H14V2M12.6667 12.6667H3.33333V3.33333H8V2H3.33333C2.97971 2 2.64057 2.14048 2.39052 2.39052C2.14048 2.64057 2 2.97971 2 3.33333V12.6667C2 13.0203 2.14048 13.3594 2.39052 13.6095C2.64057 13.8595 2.97971 14 3.33333 14H12.6667C13.0203 14 13.3594 13.8595 13.6095 13.6095C13.8595 13.3594 14 13.0203 14 12.6667V8H12.6667V12.6667Z" fill="#B078FF"/>
</svg></a>
    `;

      boxWrapper.appendChild(box);
    });

    const clones = boxWrapper.cloneNode(true);
    clones.setAttribute("data-clone", "true");
    boxWrapper.appendChild(clones);

    requestAnimationFrame(() => {
      const totalWidth = boxWrapper.scrollWidth / 2;
      const speed = 80;

      tl.current = gsap.fromTo(
        boxWrapper,
        { x: 0 },
        {
          x: -totalWidth,
          duration: totalWidth / speed,
          ease: "linear",
          repeat: -1,
        }
      );
    });

    let resumeTimeout;

    // ✅ For mobile: pause for 2 seconds on tap
    const handlePointerDown = () => {
      // Detect if it's a touch device
      if (window.matchMedia("(hover: none)").matches) {
        tl.current?.pause();
        clearTimeout(resumeTimeout);
        resumeTimeout = setTimeout(() => {
          tl.current?.resume();
        }, 2000);
      }
    };

    // ✅ For desktop: pause on hover
    const handleMouseEnter = () => {
      if (!window.matchMedia("(hover: none)").matches) {
        tl.current?.pause();
      }
    };

    const handleMouseLeave = () => {
      if (!window.matchMedia("(hover: none)").matches) {
        tl.current?.resume();
      }
    };

    container.addEventListener("pointerdown", handlePointerDown);
    container.addEventListener("mouseenter", handleMouseEnter);
    container.addEventListener("mouseleave", handleMouseLeave);

    return () => {
      tl.current?.kill();
      clearTimeout(resumeTimeout);
      container.removeEventListener("pointerdown", handlePointerDown);
      container.removeEventListener("mouseenter", handleMouseEnter);
      container.removeEventListener("mouseleave", handleMouseLeave);
    };
  }, [combinedTableData.length]);

  const handleEmailSubmit = async () => {
    const isValidEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

    if (!email || !isValidEmail) {
      setEmailError("Please enter a valid email address.");
      return;
    }

    if (!checked) {
      setEmailError("Please agree to receive updates.");
      return;
    }

    try {
      setLoadingEmail(true);
      setEmailError("");
      await sendEmail(email);
      setSubmit(false); 
    } catch (err) {
      setEmailError("Failed to subscribe. Please try again.");
      console.error(err);
    } finally {
      setLoadingEmail(false);
    }
  };

  useEffect(() => {
    if (!submit) {
      const timer = setTimeout(() => {
        setRequest(true);  // show "Request a demo" again
        setSubmit(true);   // reset form state
      }, 5000);
  
      return () => clearTimeout(timer); // cleanup on unmount
    }
  }, [submit]);
  
  return (
    <section className="mainbanner" id="home">
      <video
        className="main-banner-video"
        muted
        playsInline
        autoPlay
        loop
        width="100%"
      >
        <source src="/assets/bannerassets/luckifybg.mp4" type="video/mp4" />
      </video>

      <img
        src="/assets/bannerassets/bannertopshade.png"
        alt="bannertopshade"
        className="bannertopshade"
      />
      <img
        src={
          window.innerWidth < 991
            ? "/assets/bannerassets/bannerlinesmbl.png"
            : "/assets/bannerassets/bannerlines.png"
        }
        alt="bannerlines"
        className="bannerlines"
      />
      <span className="bottomshade"></span>

      <div className="custom-container">
        <div className="innerbanner">
          <span className="bannerspan">
            Provably fair. On-chain. Trustless.
          </span>
          <h2 className="bannerhead">Trustless Gaming Starts Now</h2>
          <p className="bannerpara">
            The future of gaming is cryptographic and decentralized. Luckify
            brings tamper-proof randomness to betting, loot boxes, and more —
            powered by Chainlink VRF and the LUCK token.
          </p>
          {request ? (
            <button
              className="bannerbtn"
              onClick={() => {
                setRequest(false);
              }}
            >
              Request a demo
            </button>
          ) : (
            <>
              {/* {submit ? (
                <div className="submitmain">
                  <div className="submitup">
                    <input
                      type="text"
                      className="submitinput"
                      placeholder="Enter your email address"
                    />
                    <button onClick={()=>{
                      setSubmit(false);
                    }} className="submitbtn">SUBMIT</button>
                  </div>
                  <div className="checkboxmain">
                    <label class="checkbox" for="yellow">
                      <input
                        type="checkbox"
                        name="color"
                        value="yellow"
                        id="yellow"
                      />
                      <span class="checkmark"></span>
                    </label>
                    <p className="checkboxpara">
                      I agree to receive emails and updates
                    </p>
                  </div>
                </div>
              ) : 
              <div className="messagemain">
                <p className="messagepara">Message sent! We will get back to you as soon as possbile!</p>
              </div>
              } */}
              {submit ? (
                <div className="submitmain">
                  <div className="submitup">
                    <input
                      type="text"
                      className="submitinput"
                      placeholder="Enter your email address"
                      value={email}
                      onChange={(e) => {
                        setEmail(e.target.value);
                        if (emailError) setEmailError(""); // Clear error as user types
                      }}
                    />
                    <button
                      className="submitbtn"
                      onClick={handleEmailSubmit}
                      disabled={loadingEmail}
                    >
                      {loadingEmail ? "Submitting..." : "SUBMIT"}
                    </button>
                  </div>

                  <div className="checkboxmain">
                    <label className="checkbox" htmlFor="yellow">
                      <input
                        type="checkbox"
                        name="color"
                        id="yellow"
                        checked={checked}
                        onChange={() => {
                          setChecked((prev) => {
                            const newValue = !prev;
                            if (newValue) setEmailError(""); // Clear error when checked
                            return newValue;
                          });
                        }}
                        
                      />
                      <span className="checkmark"></span>
                    </label>
                    <p className="checkboxpara">
                      I agree to receive emails and updates
                    </p>
                  </div>

                  {emailError && <p style={{color:"red"}} className="email-error">{emailError}</p>}
                </div>
              ) : (
                <div className="messagemain">
                  <p className="messagepara">
                    Message sent! We will get back to you as soon as possible!
                  </p>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <div className="recentmain">
        <h6 className="recenthead">RECENTLY GENERATED NUMBERS</h6>
        <div className="scroll-container" ref={containerRef}>
          <div className="box-wrapper" ref={boxWrapperRef}>
            {combinedTableData.map((item, index) => {
              const shortHash = `${item.transactionHash.slice(
                0,
                6
              )}...${item.transactionHash.slice(-4)}`;
              const formattedDate = new Date(
                item.blockTimestamp * 1000
              ).toLocaleString("en-GB", {
                day: "2-digit",
                month: "short",
                year: "2-digit",
                hour: "2-digit",
                minute: "2-digit",
              });

              return (
                <div className="scroll-box" key={index}>
                  <h6 className="boxhead">{item.result}</h6>
                  <p className="boxpara">{formattedDate}</p>
                  <a
                    href={`https://bscscan.com/tx/${item.transactionHash}`}
                    className="boxlink"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {shortHash}
                  </a>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Banner;
